/**
 * 单位换算工具函数
 * Word 使用 Twips 作为基本单位，需要转换为 CSS 可用的 px/pt
 */

// ============ 换算常量 ============

export const UNITS = {
  /** 1 pt = 20 twips */
  TWIPS_PER_PT: 20,

  /** 1 px = 0.75 pt (96 DPI 标准) */
  PT_PER_PX: 0.75,

  /** 1 px = 15 twips */
  TWIPS_PER_PX: 15,

  /** fontSize 单位: 1 pt = 2 half-points */
  HALF_POINTS_PER_PT: 2,

  /** 单倍行距对应的 line spacing 值 */
  SINGLE_LINE_SPACING: 240,
} as const;

// ============ Twips 换算 ============

/**
 * Twips 转换为像素
 * @param twips - Twips 值
 * @returns 像素值
 */
export function twipsToPx(twips: number): number {
  return twips / UNITS.TWIPS_PER_PX;
}

/**
 * 像素转换为 Twips
 * @param px - 像素值
 * @returns Twips 值
 */
export function pxToTwips(px: number): number {
  return px * UNITS.TWIPS_PER_PX;
}

/**
 * Twips 转换为 pt
 * @param twips - Twips 值
 * @returns pt 值
 */
export function twipsToPt(twips: number): number {
  return twips / UNITS.TWIPS_PER_PT;
}

/**
 * pt 转换为 Twips
 * @param pt - pt 值
 * @returns Twips 值
 */
export function ptToTwips(pt: number): number {
  return pt * UNITS.TWIPS_PER_PT;
}

// ============ Half-Points 换算 ============

/**
 * Half-points 转换为像素
 * @param halfPoints - Half-points 值 (Word fontSize 单位)
 * @returns 像素值
 */
export function halfPointsToPx(halfPoints: number): number {
  const pt = halfPoints / UNITS.HALF_POINTS_PER_PT;
  return pt / UNITS.PT_PER_PX;
}

/**
 * 像素转换为 Half-points
 * @param px - 像素值
 * @returns Half-points 值
 */
export function pxToHalfPoints(px: number): number {
  const pt = px * UNITS.PT_PER_PX;
  return pt * UNITS.HALF_POINTS_PER_PT;
}

/**
 * Half-points 转换为 pt
 * @param halfPoints - Half-points 值
 * @returns pt 值
 */
export function halfPointsToPt(halfPoints: number): number {
  return halfPoints / UNITS.HALF_POINTS_PER_PT;
}

// ============ 行高换算 ============

/**
 * Word 行距值转换为 CSS line-height
 * @param lineValue - Word 行距值
 * @param lineRule - 行距规则 ('auto' | 'exact' | 'atLeast')
 * @returns CSS line-height 值（数字或带单位的字符串）
 */
export function lineSpacingToCSS(
  lineValue: number,
  lineRule: 'auto' | 'exact' | 'atLeast' | null | undefined
): string | number {
  switch (lineRule) {
    case 'exact':
    case 'atLeast':
      // 固定行高，使用 px
      return `${twipsToPx(lineValue).toFixed(2)}px`;

    case 'auto':
    default:
      // 相对行高，240 = 单倍行距 (1.0)
      return lineValue / UNITS.SINGLE_LINE_SPACING;
  }
}

// ============ 格式化输出 ============

/**
 * Twips 转换为带单位的 CSS 像素字符串
 * @param twips - Twips 值
 * @param decimals - 小数位数，默认 2
 * @returns CSS 像素字符串，如 "29.33px"
 */
export function twipsToCSSPx(twips: number, decimals = 2): string {
  return `${twipsToPx(twips).toFixed(decimals)}px`;
}

/**
 * Half-points 转换为带单位的 CSS 像素字符串
 * @param halfPoints - Half-points 值
 * @param decimals - 小数位数，默认 2
 * @returns CSS 像素字符串
 */
export function halfPointsToCSSPx(halfPoints: number, decimals = 2): string {
  return `${halfPointsToPx(halfPoints).toFixed(decimals)}px`;
}

// ============ 显示用格式化 ============

/**
 * 格式化 Twips 值用于 UI 显示
 * @param twips - Twips 值
 * @returns 格式化字符串，如 "440 twips (29.3 px)"
 */
export function formatTwipsForDisplay(twips: number): string {
  const px = twipsToPx(twips);
  return `${twips} twips (${px.toFixed(1)} px)`;
}

/**
 * 格式化 Half-points 值用于 UI 显示
 * @param halfPoints - Half-points 值
 * @returns 格式化字符串，如 "21 (10.5 pt)"
 */
export function formatHalfPointsForDisplay(halfPoints: number): string {
  const pt = halfPointsToPt(halfPoints);
  return `${halfPoints} (${pt} pt)`;
}

// ============ 字符单位换算 ============

/**
 * 字符数转换为 Twips（基于字号）
 * @param chars - 字符数
 * @param fontSizePt - 字号（pt）
 * @returns Twips 值
 */
export function charsToTwips(chars: number, fontSizePt: number): number {
  // 字符宽度约等于字号（中文字符1em，英文字符约0.5em，这里取1em）
  return Math.round(chars * fontSizePt * UNITS.TWIPS_PER_PT);
}

/**
 * Twips 转换为字符数（基于字号）
 * @param twips - Twips 值
 * @param fontSizePt - 字号（pt）
 * @returns 字符数（保留1位小数）
 */
export function twipsToChars(twips: number, fontSizePt: number): number {
  const chars = twips / (fontSizePt * UNITS.TWIPS_PER_PT);
  return Math.round(chars * 2) / 2; // 四舍五入到0.5
}

// ============ 行单位换算 ============

/**
 * 行数转换为 Twips（基于字号和行距）
 * @param lines - 行数
 * @param fontSizePt - 字号（pt）
 * @param lineSpacingMultiplier - 行距倍数（默认1.0）
 * @returns Twips 值
 */
export function linesToTwips(
  lines: number,
  fontSizePt: number,
  lineSpacingMultiplier: number = 1.0
): number {
  const lineHeightPt = fontSizePt * lineSpacingMultiplier;
  return Math.round(lines * lineHeightPt * UNITS.TWIPS_PER_PT);
}

/**
 * Twips 转换为行数（基于字号和行距）
 * @param twips - Twips 值
 * @param fontSizePt - 字号（pt）
 * @param lineSpacingMultiplier - 行距倍数（默认1.0）
 * @returns 行数（保留1位小数）
 */
export function twipsToLines(
  twips: number,
  fontSizePt: number,
  lineSpacingMultiplier: number = 1.0
): number {
  const lineHeightPt = fontSizePt * lineSpacingMultiplier;
  const lines = twips / (lineHeightPt * UNITS.TWIPS_PER_PT);
  return Math.round(lines * 10) / 10; // 四舍五入到0.1
}
